import { motion } from "framer-motion";
import {
  ExternalLink,
  Github,
  Code,
  Cloud,
  Database,
  Globe,
} from "lucide-react";
import data from "../data/software.json";

const Projects = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
      },
    },
  };

  const cardVariants = {
    hidden: { scale: 0.9, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  const getTechnologyIcon = (tech) => {
    const techLower = tech.toLowerCase();
    if (techLower.includes("react"))
      return <Code className="w-5 h-5 text-blue-600" />;
    if (techLower.includes("node") || techLower.includes("express"))
      return <Code className="w-5 h-5 text-green-600" />;
    if (techLower.includes("aws") || techLower.includes("terraform"))
      return <Cloud className="w-5 h-5 text-orange-600" />;
    if (
      techLower.includes("database") ||
      techLower.includes("sql") ||
      techLower.includes("dynamodb")
    )
      return <Database className="w-5 h-5 text-purple-600" />;
    return <Globe className="w-5 h-5 text-gray-600" />;
  };

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-7xl mx-auto"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h1 className="font-display font-bold text-4xl md:text-5xl text-secondary-900 mb-4">
            Projects
          </h1>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            A collection of personal and professional projects showcasing my
            technical skills and passion for building innovative solutions.
          </p>
        </motion.div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {data["Projects"].map((project, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-secondary-200"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-display font-semibold text-xl text-secondary-900 mb-2">
                      {project.title}
                    </h3>
                    <p className="text-secondary-600 text-sm leading-relaxed">
                      {project.description}
                    </p>
                  </div>
                </div>

                {/* Technologies */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-secondary-700 mb-2">
                    Technologies
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, techIndex) => (
                      <div
                        key={techIndex}
                        className="flex items-center space-x-1 bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full text-xs"
                      >
                        {getTechnologyIcon(tech)}
                        <span>{tech}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Highlights */}
                <div>
                  <h4 className="text-sm font-medium text-secondary-700 mb-2">
                    Key Features
                  </h4>
                  <ul className="space-y-1">
                    {project.highlights.map((highlight, highlightIndex) => (
                      <li
                        key={highlightIndex}
                        className="flex items-start space-x-2"
                      >
                        <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-secondary-600 text-sm leading-relaxed">
                          {highlight}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Projects Section */}
        <motion.div
          variants={itemVariants}
          className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-xl p-8"
        >
          <h3 className="font-display font-semibold text-2xl text-secondary-900 mb-6 text-center">
            Open Source & Contributions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="flex items-center space-x-3 mb-3">
                <Github className="w-6 h-6 text-secondary-600" />
                <h4 className="font-semibold text-secondary-900">
                  GitHub Contributions
                </h4>
              </div>
              <p className="text-secondary-600 text-sm mb-3">
                Active contributor to various open-source projects, focusing on
                Node.js, React, and cloud infrastructure tools.
              </p>
              <a
                href="https://github.com/rohitnsaigal"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors duration-200"
              >
                <span className="text-sm font-medium">View Profile</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="flex items-center space-x-3 mb-3">
                <Code className="w-6 h-6 text-secondary-600" />
                <h4 className="font-semibold text-secondary-900">
                  Technical Writing
                </h4>
              </div>
              <p className="text-secondary-600 text-sm mb-3">
                Sharing knowledge through technical blog posts, documentation,
                and mentoring junior developers.
              </p>
              <a
                href="https://medium.com/@rohitnsaigal"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors duration-200"
              >
                <span className="text-sm font-medium">Read Articles</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div variants={itemVariants} className="text-center mt-16">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="font-display font-semibold text-2xl text-secondary-900 mb-4">
              Interested in Collaborating?
            </h3>
            <p className="text-secondary-600 mb-6 max-w-2xl mx-auto">
              I'm always open to discussing new project opportunities,
              especially in health tech, backend engineering, and cloud
              infrastructure. Let's build something amazing together.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="mailto:rohitnsaigal@gmail.com"
                className="inline-flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
              >
                <span>Start a Project</span>
                <ExternalLink className="w-5 h-5" />
              </a>
              <a
                href="https://www.linkedin.com/in/rohit-saigal"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 border border-secondary-300 hover:border-primary-600 text-secondary-700 hover:text-primary-600 px-6 py-3 rounded-lg font-medium transition-colors duration-200"
              >
                <span>Connect on LinkedIn</span>
                <ExternalLink className="w-5 h-5" />
              </a>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Projects;
